import { useState } from 'react';
import { MoreVertical, Phone, Video, Send, Paperclip, Smile, Calendar, Users, DollarSign, ExternalLink, Image as ImageIcon, ChevronDown, ChevronRight, Clock, FolderKanban, CheckCircle2, Clock3, AlertCircle, Package } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { SecondarySidebar, SecondarySidebarHeader, SecondarySidebarSearch } from './SecondarySidebar';

interface Message {
  id: number;
  text: string;
  sender: 'me' | 'them';
  timestamp: string;
}

interface Chat {
  id: number;
  creator: {
    name: string;
    avatar: string;
    platform: string;
  };
  campaign: {
    name: string;
    status: 'Active' | 'Draft' | 'Completed';
    budget: string;
    startDate: string;
    endDate: string;
    deliverables: string[];
    image: string;
    brandLogo: string;
    brandName: string;
  };
  draftStatus: 'approved' | 'pending' | 'not-ready';
  productDelivered: boolean;
  lastMessage: string;
  timestamp: string;
  unread: number;
  messages: Message[];
}

const mockChats: Chat[] = [
  {
    id: 1,
    creator: {
      name: 'Sarah Martinez',
      avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100',
      platform: 'Instagram',
    },
    campaign: {
      name: 'Summer Beauty Campaign',
      status: 'Active',
      budget: '$5,000',
      startDate: 'Jun 1, 2024',
      endDate: 'Aug 31, 2024',
      deliverables: ['3 Instagram Reels', '5 Stories', '2 Static Posts'],
      image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400',
      brandLogo: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400',
      brandName: 'Beauty Brand',
    },
    draftStatus: 'approved',
    productDelivered: false,
    lastMessage: 'Perfect! I can start creating content next week.',
    timestamp: '2m ago',
    unread: 2,
    messages: [
      { id: 1, text: 'Hi Sarah! Thanks for accepting the campaign invite.', sender: 'me', timestamp: '10:30 AM' },
      { id: 2, text: 'Hi! I\'m so excited to work on this campaign!', sender: 'them', timestamp: '10:32 AM' },
      { id: 3, text: 'Great! Let me know if you have any questions about the brief.', sender: 'me', timestamp: '10:35 AM' },
      { id: 4, text: 'I reviewed the brief. When do you need the first content piece?', sender: 'them', timestamp: '10:45 AM' },
      { id: 5, text: 'We\'d love to have the first reel by June 15th. Does that work for you?', sender: 'me', timestamp: '10:50 AM' },
      { id: 6, text: 'Perfect! I can start creating content next week.', sender: 'them', timestamp: '10:52 AM' },
    ],
  },
  {
    id: 2,
    creator: {
      name: 'Mike Johnson',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100',
      platform: 'TikTok',
    },
    campaign: {
      name: 'Tech Product Launch',
      status: 'Draft',
      budget: '$8,500',
      startDate: 'Jul 1, 2024',
      endDate: 'Sep 30, 2024',
      deliverables: ['4 TikTok Videos', '10 Stories', '3 YouTube Shorts'],
      image: 'https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?w=400',
      brandLogo: 'https://images.unsplash.com/photo-1526948531399-320e7e40f0ca?w=400',
      brandName: 'Tech Brand',
    },
    draftStatus: 'pending',
    productDelivered: false,
    lastMessage: 'Can we discuss the creative direction?',
    timestamp: '1h ago',
    unread: 0,
    messages: [
      { id: 1, text: 'Hey Mike! We\'d love to collaborate with you on our tech launch.', sender: 'me', timestamp: '9:15 AM' },
      { id: 2, text: 'Sounds interesting! Tell me more about the product.', sender: 'them', timestamp: '9:20 AM' },
      { id: 3, text: 'Can we discuss the creative direction?', sender: 'them', timestamp: '9:45 AM' },
    ],
  },
  {
    id: 3,
    creator: {
      name: 'Emma Chen',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100',
      platform: 'Instagram',
    },
    campaign: {
      name: 'Fitness Brand Collab',
      status: 'Active',
      budget: '$6,200',
      startDate: 'May 15, 2024',
      endDate: 'Jul 15, 2024',
      deliverables: ['2 Instagram Reels', '8 Stories', '1 IGTV'],
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400',
      brandLogo: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400',
      brandName: 'Fitness Brand',
    },
    draftStatus: 'approved',
    productDelivered: false,
    lastMessage: 'Just submitted the draft for review!',
    timestamp: '3h ago',
    unread: 1,
    messages: [
      { id: 1, text: 'Emma, we love your content style!', sender: 'me', timestamp: '8:00 AM' },
      { id: 2, text: 'Thank you! I\'m excited about this partnership.', sender: 'them', timestamp: '8:10 AM' },
      { id: 3, text: 'Just submitted the draft for review!', sender: 'them', timestamp: '2:30 PM' },
    ],
  },
  {
    id: 4,
    creator: {
      name: 'Alex Rivera',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100',
      platform: 'YouTube',
    },
    campaign: {
      name: 'Travel Campaign Q3',
      status: 'Completed',
      budget: '$12,000',
      startDate: 'Apr 1, 2024',
      endDate: 'Jun 30, 2024',
      deliverables: ['2 YouTube Videos', '5 Shorts', '10 Instagram Posts'],
      image: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400',
      brandLogo: 'https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400',
      brandName: 'Travel Brand',
    },
    draftStatus: 'approved',
    productDelivered: true,
    lastMessage: 'Thanks for the great collaboration!',
    timestamp: '2d ago',
    unread: 0,
    messages: [
      { id: 1, text: 'Alex, excited to work with you on this travel campaign!', sender: 'me', timestamp: 'Mon 10:00 AM' },
      { id: 2, text: 'Thanks for the great collaboration!', sender: 'them', timestamp: 'Fri 3:00 PM' },
    ],
  },
  {
    id: 5,
    creator: {
      name: 'Jessica Lee',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=100',
      platform: 'Instagram',
    },
    campaign: {
      name: 'Summer Beauty Campaign',
      status: 'Active',
      budget: '$5,000',
      startDate: 'Jun 1, 2024',
      endDate: 'Aug 31, 2024',
      deliverables: ['2 Instagram Reels', '3 Stories'],
      image: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400',
      brandLogo: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400',
      brandName: 'Beauty Brand',
    },
    draftStatus: 'approved',
    productDelivered: false,
    lastMessage: 'Looking forward to this!',
    timestamp: '5h ago',
    unread: 0,
    messages: [
      { id: 1, text: 'Hi Jessica! Welcome to the Summer Beauty Campaign team!', sender: 'me', timestamp: 'Yesterday 2:00 PM' },
      { id: 2, text: 'Looking forward to this!', sender: 'them', timestamp: 'Yesterday 2:15 PM' },
    ],
  },
];

export function MessagesPage() {
  const [selectedChat, setSelectedChat] = useState<Chat | null>(mockChats[0]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<'recent' | 'campaign'>('recent');
  const [expandedCampaigns, setExpandedCampaigns] = useState<string[]>([]);

  const filteredChats = mockChats.filter(chat =>
    chat.creator.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    chat.campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group chats by campaign
  const chatsByCampaign = filteredChats.reduce((acc, chat) => {
    const campaignName = chat.campaign.name;
    if (!acc[campaignName]) {
      acc[campaignName] = {
        campaign: chat.campaign,
        chats: [],
      };
    }
    acc[campaignName].chats.push(chat);
    return acc;
  }, {} as Record<string, { campaign: Chat['campaign']; chats: Chat[] }>);

  const toggleCampaign = (campaignName: string) => {
    setExpandedCampaigns(prev =>
      prev.includes(campaignName)
        ? prev.filter(c => c !== campaignName)
        : [...prev, campaignName]
    );
  };

  const handleSendMessage = () => {
    if (messageInput.trim() && selectedChat) {
      // Add message logic here
      if (import.meta.env.DEV) console.log('Sending message:', messageInput);
      setMessageInput('');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-700';
      case 'Draft':
        return 'bg-yellow-100 text-yellow-700';
      case 'Completed':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="h-screen bg-[#f8f9fa] flex">
      {/* Left Sidebar - Chat List */}
      <SecondarySidebar width="w-80">
        {/* Header */}
        <SecondarySidebarHeader>
          <h2 className="text-gray-900 mb-3">Messages</h2>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-2 mb-3">
            <button
              onClick={() => setViewMode('recent')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === 'recent'
                  ? 'bg-[#be43ff] text-white'
                  : 'bg-gray-200/60 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <Clock className="w-4 h-4" />
              Recent
            </button>
            <button
              onClick={() => setViewMode('campaign')}
              className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                viewMode === 'campaign'
                  ? 'bg-[#be43ff] text-white'
                  : 'bg-gray-200/60 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <FolderKanban className="w-4 h-4" />
              Campaign
            </button>
          </div>
          
          <SecondarySidebarSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search conversations..."
          />
        </SecondarySidebarHeader>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {viewMode === 'recent' ? (
            filteredChats.map((chat) => (
              <button
                key={chat.id}
                onClick={() => setSelectedChat(chat)}
                className={`w-full p-4 border-b border-gray-200/50 hover:bg-gray-100/60 transition-colors text-left ${
                  selectedChat?.id === chat.id ? 'bg-[#be43ff]/10 border-l-4 border-l-[#be43ff]' : ''
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={chat.creator.avatar}
                      alt={chat.creator.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    {chat.unread > 0 && (
                      <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#a9f600] text-[#2e024c] text-xs rounded-full flex items-center justify-center">
                        {chat.unread}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="text-sm text-gray-900 truncate">{chat.creator.name}</h3>
                      <span className="text-xs text-gray-400 flex-shrink-0">{chat.timestamp}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-1 truncate">{chat.campaign.name}</p>
                    <p className="text-xs text-gray-400 truncate">{chat.lastMessage}</p>
                  </div>
                </div>
              </button>
            ))
          ) : (
            Object.entries(chatsByCampaign).map(([campaignName, { campaign, chats }]) => {
              const isExpanded = expandedCampaigns.includes(campaignName);
              const totalUnread = chats.reduce((sum, chat) => sum + chat.unread, 0);
              
              return (
                <div key={campaignName}>
                  {/* Campaign Folder Header */}
                  <button
                    className={`w-full p-4 border-b border-gray-200/50 hover:bg-gray-100/60 transition-colors text-left ${
                      isExpanded ? 'bg-gray-100/80' : ''
                    }`}
                    onClick={() => toggleCampaign(campaignName)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        {/* Campaign Image with Folder Overlay */}
                        <div className="relative">
                          <img
                            src={campaign.image}
                            alt={campaign.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          {/* Folder Badge */}
                          <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#be43ff] rounded-full flex items-center justify-center shadow-sm">
                            <FolderKanban className="w-3 h-3 text-white" />
                          </div>
                        </div>
                        {totalUnread > 0 && (
                          <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#a9f600] text-[#2e024c] text-xs rounded-full flex items-center justify-center">
                            {totalUnread}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <h3 className="text-sm text-gray-900 truncate">{campaign.name}</h3>
                            <div className={`flex-shrink-0 px-2 py-0.5 rounded text-[10px] ${getStatusColor(campaign.status)}`}>
                              {campaign.status}
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-[#be43ff] mb-1 flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {chats.length} {chats.length === 1 ? 'creator' : 'creators'}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {chats.reduce((latest, chat) => {
                            return new Date(chat.timestamp) > new Date(latest.timestamp) ? chat : latest;
                          }).lastMessage}
                        </p>
                      </div>
                    </div>
                  </button>
                  
                  {/* Nested Creator Chats */}
                  {isExpanded && (
                    <div className="bg-gray-50/50">
                      {chats.map((chat) => (
                        <button
                          key={chat.id}
                          onClick={() => setSelectedChat(chat)}
                          className={`w-full p-4 pl-12 border-b border-gray-200/50 hover:bg-gray-100/60 transition-colors text-left relative ${
                            selectedChat?.id === chat.id ? 'bg-[#be43ff]/10 border-l-4 border-l-[#be43ff]' : ''
                          }`}
                        >
                          {/* Indentation Line */}
                          <div className="absolute left-8 top-0 bottom-0 w-px bg-[#be43ff]/30"></div>
                          
                          <div className="flex items-start gap-3">
                            <div className="relative flex-shrink-0">
                              <img
                                src={chat.creator.avatar}
                                alt={chat.creator.name}
                                className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 shadow-sm"
                              />
                              {chat.unread > 0 && (
                                <div className="absolute -top-1 -right-1 w-5 h-5 bg-[#a9f600] text-[#2e024c] text-xs rounded-full flex items-center justify-center">
                                  {chat.unread}
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between mb-1">
                                <h3 className="text-sm text-gray-900 truncate">{chat.creator.name}</h3>
                                <span className="text-xs text-gray-400 flex-shrink-0">{chat.timestamp}</span>
                              </div>
                              <p className="text-xs text-gray-500 mb-1 truncate">{chat.creator.platform}</p>
                              <p className="text-xs text-gray-400 truncate">{chat.lastMessage}</p>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </SecondarySidebar>

      {/* Main Content Card */}
      <div className="flex-1 flex flex-col p-2.5 pl-2.5 min-w-0">
        <div className="flex-1 flex rounded-2xl bg-white overflow-hidden border border-gray-200/80 shadow-sm min-h-0">
          {/* Middle - Chat Area */}
          {selectedChat ? (
            <div className="flex-1 flex flex-col min-w-0">
              {/* Chat Header */}
              <div className="p-4 border-b border-gray-200 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img
                    src={selectedChat.creator.avatar}
                    alt={selectedChat.creator.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-sm text-gray-900">{selectedChat.creator.name}</h3>
                    <p className="text-xs text-gray-500">{selectedChat.creator.platform}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Phone className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <Video className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <MoreVertical className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {selectedChat.messages.map((message) => (
                  <div
                    key={message.id}
                    className={`flex ${message.sender === 'me' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-md px-4 py-2.5 rounded-2xl ${
                        message.sender === 'me'
                          ? 'bg-purple-600 text-white rounded-br-sm'
                          : 'bg-gray-100 text-gray-900 rounded-bl-sm'
                      }`}
                    >
                      <p className="text-sm">{message.text}</p>
                      <p
                        className={`text-xs mt-1 ${
                          message.sender === 'me' ? 'text-purple-200' : 'text-gray-500'
                        }`}
                      >
                        {message.timestamp}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t border-gray-200">
                <div className="flex items-end gap-2">
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                    <Paperclip className="w-5 h-5 text-gray-600" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0">
                    <Smile className="w-5 h-5 text-gray-600" />
                  </button>
                  <textarea
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    placeholder="Type a message..."
                    rows={1}
                    className="flex-1 px-4 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    className="p-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Send className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-gray-900 mb-2">Select a conversation</h3>
                <p className="text-sm text-gray-500">Choose a chat from the list to start messaging</p>
              </div>
            </div>
          )}

          {/* Right Sidebar - Campaign Details */}
          {selectedChat && (
            <div className="w-80 border-l border-gray-200 overflow-y-auto">
              <div className="p-6">
                {/* Campaign Image */}
                <div className="mb-6">
                  <div className="aspect-video rounded-lg overflow-hidden mb-3">
                    <ImageWithFallback
                      src={selectedChat.campaign.image}
                      alt={selectedChat.campaign.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Campaign Name and Brand Logo */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base text-gray-900 mb-2">{selectedChat.campaign.name}</h3>
                      <div className={`inline-block px-2 py-1 rounded-full text-xs ${getStatusColor(selectedChat.campaign.status)}`}>
                        {selectedChat.campaign.status}
                      </div>
                    </div>
                    {/* Brand Logo */}
                    <div className="flex-shrink-0 ml-3">
                      <img
                        src={selectedChat.campaign.brandLogo}
                        alt={selectedChat.campaign.brandName}
                        className="w-12 h-12 rounded-lg object-cover border border-gray-200"
                        title={selectedChat.campaign.brandName}
                      />
                    </div>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg space-y-3">
                  <h4 className="text-xs text-gray-600 uppercase tracking-wide mb-3">Status</h4>
                  
                  {/* Draft Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-full ${
                        selectedChat.draftStatus === 'approved' 
                          ? 'bg-green-100' 
                          : selectedChat.draftStatus === 'pending'
                          ? 'bg-yellow-100'
                          : 'bg-gray-100'
                      }`}>
                        {selectedChat.draftStatus === 'approved' ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : selectedChat.draftStatus === 'pending' ? (
                          <Clock3 className="w-4 h-4 text-yellow-600" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <span className="text-sm text-gray-900">Draft</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      selectedChat.draftStatus === 'approved'
                        ? 'bg-green-100 text-green-700'
                        : selectedChat.draftStatus === 'pending'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedChat.draftStatus === 'approved' 
                        ? 'Approved' 
                        : selectedChat.draftStatus === 'pending'
                        ? 'Pending'
                        : 'Not Ready'}
                    </span>
                  </div>

                  {/* Product Delivered Status */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`p-1.5 rounded-full ${
                        selectedChat.productDelivered ? 'bg-green-100' : 'bg-gray-100'
                      }`}>
                        {selectedChat.productDelivered ? (
                          <CheckCircle2 className="w-4 h-4 text-green-600" />
                        ) : (
                          <Package className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      <span className="text-sm text-gray-900">Product Delivered</span>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      selectedChat.productDelivered
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {selectedChat.productDelivered ? 'Delivered' : 'Pending'}
                    </span>
                  </div>
                </div>

                {/* Campaign Details */}
                <div className="space-y-4 mb-6">
                  <div className="pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <DollarSign className="w-4 h-4" />
                      <span className="text-xs">Budget</span>
                    </div>
                    <p className="text-sm text-gray-900">{selectedChat.campaign.budget}</p>
                  </div>

                  <div className="pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-xs">Timeline</span>
                    </div>
                    <p className="text-sm text-gray-900">
                      {selectedChat.campaign.startDate} - {selectedChat.campaign.endDate}
                    </p>
                  </div>

                  <div className="pb-4 border-b border-gray-100">
                    <div className="flex items-center gap-2 text-gray-600 mb-2">
                      <ImageIcon className="w-4 h-4" />
                      <span className="text-xs">Deliverables</span>
                    </div>
                    <ul className="space-y-1.5">
                      {selectedChat.campaign.deliverables.map((deliverable, idx) => (
                        <li key={idx} className="text-sm text-gray-900 flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span>
                          {deliverable}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Creator Info */}
                <div className="space-y-3">
                  <h4 className="text-sm text-gray-900">Creator Profile</h4>
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3 mb-3">
                      <img
                        src={selectedChat.creator.avatar}
                        alt={selectedChat.creator.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div className="flex-1">
                        <h5 className="text-sm text-gray-900">{selectedChat.creator.name}</h5>
                        <p className="text-xs text-gray-500">{selectedChat.creator.platform}</p>
                      </div>
                    </div>
                    <button className="w-full px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4" />
                      View Full Profile
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}